import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authAPI } from "../api";
import { teacherAPI } from "../api/teacher.api";
import { setToken, getToken, removeToken } from "../utils/token";
import {
  setStoredUser,
  getStoredUser,
  removeStoredUser,
} from "../utils/authStorage";

const AuthContext = createContext(null);

function resolveTeacherProfile(payload) {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const source =
    payload.user && typeof payload.user === "object" && !Array.isArray(payload.user)
      ? payload.user
      : payload;

  if (!source.permissions && payload.permissions) {
    return { ...source, permissions: payload.permissions };
  }

  return source;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setSession = useCallback(({ accessToken, user: nextUser }) => {
    setToken(accessToken);
    setStoredUser(nextUser);
    setUser(nextUser);
  }, []);

  const refreshTeacher = useCallback(async () => {
    const tokenAtStart = getToken();

    if (!tokenAtStart) {
      return null;
    }

    const profile = resolveTeacherProfile(await teacherAPI.getMe());

    if (!profile || getToken() !== tokenAtStart) {
      return null;
    }

    setUser((prev) => {
      const merged = { ...(prev || {}), ...profile };
      setStoredUser(merged);
      return merged;
    });

    return profile;
  }, []);

  const clearSession = useCallback(() => {
    removeToken();
    removeStoredUser();
    setUser(null);
  }, []);

  useEffect(() => {
    const token = getToken();
    const storedUser = getStoredUser();

    if (token && storedUser) {
      setUser(storedUser);
    }

    if (token) {
      refreshTeacher().catch(() => {});
    }

    setLoading(false);
  }, [refreshTeacher]);

  const loginWithPassword = useCallback(
    async ({ phone, password }) => {
      const data = await authAPI.loginWithPassword({ phone, password });
      setSession(data);
      refreshTeacher().catch(() => {});
      return data;
    },
    [refreshTeacher, setSession]
  );

  const requestOtp = useCallback(async ({ phone }) => {
    await authAPI.requestOtp({ phone });
  }, []);

  const verifyOtp = useCallback(
    async ({ phone, code }) => {
      const data = await authAPI.verifyOtp({ phone, code });
      setSession(data);
      refreshTeacher().catch(() => {});
      return data;
    },
    [refreshTeacher, setSession]
  );

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn: !!user,
        loading,
        loginWithPassword,
        requestOtp,
        verifyOtp,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
